import { ButtonAsLink } from "@made-people/blueprint-button";
import { useTranslate } from "@made-people/grease-translations";
import { Truck } from "../../../../assets/icons/Truck";
import { useFrontendContext } from "../../../../hooks/use-frontend-context";
import type { ReturnAddressTable } from "./non-inretrn-return-flow";
import { readRichText } from "./return-step-heading";
import { getReturnAddress } from "./show-summary";

type ReturnSuccessProps = {
  returnCode: string;
  deliveryCountry: string;
  instructions?: string;
  returnAddress: ReturnAddressTable;
};

const RETURN_CODE_LABEL_KEY = "return_flow_success_return_code_label";

const RichText = ({ value }: { value?: string }) => {
  const html = readRichText(value);

  if (!html) {
    return null;
  }

  return (
    <div
      className="font-primary text-body-base text-black [&_li]:mt-4 [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:m-0 [&_ul]:list-disc [&_ul]:pl-5"
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
};

const Address = ({ value }: { value: string }) => {
  const lines = value
    .split("|")
    .map((line) => line.trim())
    .filter((line) => line !== "");

  return (
    <p className="font-primary text-body-sm text-black">
      {lines.map((line) => (
        <span key={line} className="block">
          {line}
        </span>
      ))}
    </p>
  );
};

export const ReturnSuccess = ({
  returnCode,
  deliveryCountry,
  instructions,
  returnAddress,
}: ReturnSuccessProps) => {
  const translate = useTranslate();
  const frontendContext = useFrontendContext();
  const returnCodeLabel = translate(RETURN_CODE_LABEL_KEY);
  const countryCode = deliveryCountry || frontendContext.country;
  const address = getReturnAddress(returnAddress, countryCode);

  return (
    <div className="mt-component-spacing-2xl mb-component-spacing-5xl px-component-spacing-sm">
      <div className="mx-auto flex max-w-[920px] flex-col gap-8">
        <div
          className="mx-auto flex h-[120px] w-full max-w-[500px] items-center justify-center rounded-[5px] border-2 border-sand-200 bg-sand-100"
          aria-label={returnCodeLabel}
        >
          <p className="font-primary text-heading-xl text-black">
            {returnCode}
          </p>
        </div>
        <RichText value={instructions} />
        {address && (
          <div className="flex items-start justify-center gap-4 rounded-md bg-surface-1 px-5 pt-6 pb-component-spacing-lg">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white">
              <Truck className="h-5 w-5 text-black" aria-hidden />
            </span>
            <Address value={address} />
          </div>
        )}
        <div className="mx-auto w-full max-w-[500px] pt-component-spacing-xl">
          <ButtonAsLink
            label={translate("return_flow_success_homepage_button")}
            link={{ href: "" }}
            variants={{
              width: "full",
              rounded: "md",
              color: "black",
              size: "lg",
            }}
          />
        </div>
      </div>
    </div>
  );
};
